export enum L10N {
  EN = "en",
  FR = "fr",
  ES = "es",
  AR = "ar",
  SW = "sw",
  KAM = "kam",
}
type L10nValues = (typeof L10N)[keyof typeof L10N];

export type I18nString = {
  [K in L10N]?: string;
};
type ExcludeTypes = Date;

export type TranslatedI18nObject<T extends object> = Equals<
  T,
  I18nString
> extends true
  ? string
  : {
      [K in keyof T]: T[K] extends ExcludeTypes
        ? T[K]
        : Equals<T[K], I18nString> extends true
          ? // if the parameter is an I18nString, cast to string
            string
          : Equals<T[K], Array<I18nString>> extends true // if it's an array of I18nString, cast to array of string
            ? string[]
            : T[K] extends (infer A)[] // if it's an array, translate each element
              ? A extends object
                ? TranslatedI18nObject<A>[]
                : A[]
              : T[K] extends object // if it's an object, recursively cast its params
                ? TranslatedI18nObject<T[K]>
                : T[K]; // else keep the original value
    };

export type Equals<T, U, Y = true, N = false> = (<G>() => G extends T
  ? 1
  : 2) extends <G>() => G extends U ? 1 : 2
  ? Y
  : N;

export function mapI18n(language: L10nValues): (obj: any) => string {
  return (obj): string => {
    if (typeof obj === "object" && obj !== null) {
      const languageValue = obj[language];
      /**
       * Opinionated decision to always try return at least the 'en'
       * translation of something - if the 'en' attribute is not present
       * then we should return `null` as returning an empty string would
       * be considered as valid by the graphql type `String!` and that
       * may .
       */
      return languageValue || obj.en || null;
    }

    return obj;
  };
}

const l10nValuesList = Object.values(L10N) as string[];

function isI18nObject<T extends Record<string, unknown>>(
  maybeI18n: T,
): T extends I18nString ? true : false {
  const keys = Object.keys(maybeI18n);
  const nonI18nKeys = keys.filter(
    (key) => !l10nValuesList.includes(key) && !key.includes("typename"),
  );
  return (nonI18nKeys.length === 0) as T extends I18nString ? true : false;
}

// We need to use object, as classes (eg mongoose models) don't have an index signature, so Record<string, unknown> does not work

/**
 * Due to a TS limitation with Generics we need to keep using explicit casting on the return type of this function.
 *
 * For reference see:
 * https://github.com/microsoft/TypeScript/issues/31661#issuecomment-497138929
 */
export function translateI18nObject<T extends object>(
  schema: T,
  language: L10nValues,
): TranslatedI18nObject<T> {
  const getI18n = mapI18n(language);

  const objToMutate = { ...schema } as Record<string, unknown>;

  Object.entries(schema).forEach(([key, entry]) => {
    if (Array.isArray(entry)) {
      const translatedArray = entry.map((i) => {
        if (
          typeof i === "object" &&
          i.constructor === Object &&
          !isI18nObject(i)
        ) {
          return translateI18nObject(i, language);
        }

        return getI18n(i);
      });
      objToMutate[key] = translatedArray;
      return;
    }

    // if it's an object, but an instance of a class other than Object, leave it alone
    if (entry && typeof entry === "object" && entry.constructor !== Object) {
      return;
    }

    if (entry && isI18nObject(entry)) {
      objToMutate[key] = getI18n(entry);
      return;
    }

    // Recursively translate plain JS objects
    if (entry && typeof entry === "object") {
      objToMutate[key] = translateI18nObject(
        entry as Record<string, unknown>,
        language,
      );
    }
  });

  return objToMutate as TranslatedI18nObject<T>;
}
